import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserGraduate,
  faKey,
  faFileCsv,
  faUsers,
  faEye,
  faChevronLeft,
  faChevronRight,
  faRefresh,
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { streetlearnApi } from '../api';
import { showToast } from '../../components/toast';
import GrantModal from './GrantModal';
import BulkGrantModal from './BulkGrantModal';
import { API_URL } from '../../config';

const LearnersList = () => {
  const navigate = useNavigate();
  const [learners, setLearners] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);

  // Filters
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [entitled, setEntitled] = useState('');
  const [hasSubscriber, setHasSubscriber] = useState('');

  // Modals
  const [selectedLearner, setSelectedLearner] = useState(null);
  const [showGrantModal, setShowGrantModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);

  const fetchLearners = useCallback(async () => {
    try {
      setLoading(true);
      const data = await streetlearnApi.getLearners({
        page,
        limit,
        q: search.trim() || undefined,
        status: status || undefined,
        entitled: entitled !== '' ? entitled : undefined,
        has_subscriber: hasSubscriber !== '' ? hasSubscriber : undefined,
      });
      setLearners(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      showToast.error(err.response?.data?.message || 'Failed to load learners');
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, status, entitled, hasSubscriber]);

  useEffect(() => {
    fetchLearners();
  }, [fetchLearners]);

  const handleExportCsv = () => {
    const token = localStorage.getItem('authToken');
    const params = new URLSearchParams();
    if (search.trim()) params.append('q', search.trim());
    if (status) params.append('status', status);
    if (entitled !== '') params.append('entitled', entitled);
    if (hasSubscriber !== '') params.append('has_subscriber', hasSubscriber);
    if (token) params.append('token', token);

    window.open(`${API_URL}streetlearn/learners/export?${params.toString()}`, '_blank');
  };

  const totalPages = Math.ceil(total / limit) || 1;
  const start = total === 0 ? 0 : (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  return (
    <div className="mt-5">
      {/* Top Header Section (Aligned with Subscriber Module Layout) */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
        <div>
          <h4 className="panel-title m-0">
            <FontAwesomeIcon icon={faUserGraduate} className="text-primary me-2" />
            StreetLearn Learners
          </h4>
        </div>
        <div className="d-flex align-items-center flex-wrap">
          <button className="btn btn-default btn-sm mx-1" onClick={fetchLearners} title="Refresh">
            <FontAwesomeIcon icon={faRefresh} spin={loading} /> Refresh
          </button>
          <button className="btn btn-default btn-sm mx-1" onClick={handleExportCsv}>
            <FontAwesomeIcon icon={faFileCsv} /> Export CSV
          </button>
          <button className="btn btn-primary btn-sm mx-1" onClick={() => setShowBulkModal(true)}>
            <FontAwesomeIcon icon={faUsers} /> Bulk Grant Access
          </button>

          {total > 0 && (
            <div className="d-inline-flex align-items-center ms-2">
              <span style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }} className="me-2">
                {start} to {end} of {total}
              </span>
              <button
                className="btn btn-default btn-sm"
                title="Previous"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              &nbsp;
              <button
                className="btn btn-default btn-sm"
                title="Next"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Main Table Panel (Exact Subscriber Module UI) */}
      <div className="panel panel-default with-rounded-corner with-shadow mb-3">
        <div className="panel-body p-0">
          <div className="table-responsive">
            <table className="table table-condensed table-hover bg-white sticky-table m-0">
              <thead>
                <tr>
                  <th>Learner Details</th>
                  <th>Learning Progress</th>
                  <th style={{ minWidth: '120px' }}>Status</th>
                  <th style={{ minWidth: '130px' }}>Entitlement</th>
                  <th style={{ minWidth: '120px' }}>Registered</th>
                  <th style={{ width: '90px', textAlign: 'center' }}>Actions</th>
                </tr>
                {/* 🔎 In-Table Search Filter Row (Exact Subscriber Input & Select Style) */}
                <tr>
                  <th>
                    <input
                      type="text"
                      placeholder=""
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(1);
                      }}
                      style={{
                        width: '100%',
                        padding: '5px',
                        fontSize: '12px',
                      }}
                    />
                  </th>
                  <th></th>
                  <th>
                    <select
                      value={status}
                      onChange={(e) => {
                        setStatus(e.target.value);
                        setPage(1);
                      }}
                      style={{ width: '100%', padding: '5px', fontSize: '12px' }}
                    >
                      <option value="">--select--</option>
                      <option value="active">Active</option>
                      <option value="blocked">Blocked</option>
                    </select>
                  </th>
                  <th>
                    <select
                      value={entitled}
                      onChange={(e) => {
                        setEntitled(e.target.value);
                        setPage(1);
                      }}
                      style={{ width: '100%', padding: '5px', fontSize: '12px' }}
                    >
                      <option value="">--select--</option>
                      <option value="1">Lifetime</option>
                      <option value="0">Free</option>
                    </select>
                  </th>
                  <th></th>
                  <th style={{ textAlign: 'center' }}>
                    <button
                      type="button"
                      className="btn btn-default btn-xs"
                      title="Clear all filters"
                      onClick={() => {
                        setSearch('');
                        setStatus('');
                        setEntitled('');
                        setHasSubscriber('');
                        setPage(1);
                      }}
                    >
                      Clear
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="text-center py-4">
                      <FontAwesomeIcon icon={faRefresh} spin className="me-2 text-primary" />
                      Loading learners...
                    </td>
                  </tr>
                ) : learners.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-4 text-muted">
                      No learners found matching the search criteria.
                    </td>
                  </tr>
                ) : (
                  learners.map((l) => (
                    <tr key={l.id}>
                      <td>
                        <div className="font-bold text-dark">{l.phone}</div>
                        {l.name && <div className="text-muted small">{l.name}</div>}
                        {l.email && <div className="text-muted small">{l.email}</div>}
                      </td>
                      <td>
                        <div style={{ fontSize: '12px' }}>
                          Lessons: <strong>{l.lessons_done}</strong>/147 &bull; Exams:{' '}
                          <strong>{l.exams_passed}</strong>/16
                          {l.certificates > 0 && (
                            <span className="ms-2 text-warning font-weight-bold">
                              &bull; {l.certificates} Cert
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        {l.status === 'active' ? (
                          <span className="text-success font-weight-bold">Active</span>
                        ) : (
                          <span className="text-danger font-weight-bold">Blocked</span>
                        )}
                      </td>
                      <td>
                        {l.entitled ? (
                          <span className="text-primary font-weight-bold">
                            Lifetime ({l.entitlement_source || 'active'})
                          </span>
                        ) : (
                          <span className="text-muted">Free</span>
                        )}
                      </td>
                      <td className="text-muted small">
                        {l.created_at ? new Date(l.created_at).toLocaleDateString() : '-'}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <button
                          className="btn btn-default btn-xs mx-1"
                          title="View Learner Profile"
                          onClick={() => navigate(`/streetlearn/learners/${l.id}`)}
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </button>
                        {!l.entitled && (
                          <button
                            className="btn btn-success btn-xs mx-1"
                            title="Grant Lifetime Access"
                            onClick={() => {
                              setSelectedLearner(l);
                              setShowGrantModal(true);
                            }}
                          >
                            <FontAwesomeIcon icon={faKey} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Grant Access Modal */}
      {selectedLearner && (
        <GrantModal
          show={showGrantModal}
          learner={selectedLearner}
          onHide={() => {
            setShowGrantModal(false);
            setSelectedLearner(null);
          }}
          onSuccess={fetchLearners}
        />
      )}

      {/* Bulk Grant Modal */}
      <BulkGrantModal
        show={showBulkModal}
        onHide={() => setShowBulkModal(false)}
        onSuccess={fetchLearners}
      />
    </div>
  );
};

export default LearnersList;
